/** Top filters for projects page using FilterStripLayout + collapsible additional row. */
import React, { useMemo } from 'react';
import { InlineSpinner } from '../../common/loading';

import { Box, Chip, alpha, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ProjectsFilterOptions, ProjectsFilterRequest } from '../../../types/projects';
import { excludeUnallocatedFromBus } from '../../../constants/buConstants';
import { QUARTERS } from '../../../constants/ProjectConstants';
import { useAuth } from '../../../contexts/AuthContext';
import { hasAdminAccess } from '../../../utils/AdminLogic';
import { sortBusinessUnits } from '../../../utils/buUtils';
import { SEARCE_COLORS } from '../../../theme';
import SelectAllMultiSelect from '../../common/SelectAllMultiSelect';
import FilterStripLayout from '../FilterStripLayout';

interface ProjectsTopFiltersProps {
  currentFilters: ProjectsFilterRequest;
  setCurrentFilters: React.Dispatch<React.SetStateAction<ProjectsFilterRequest>>;
  filterOptions: ProjectsFilterOptions;
  availableSubBus: string[];
  filterOptionsLoading: boolean;
  subBuLoading: boolean;
  filterOptionsError: any;
  refetchFilters: () => void;
  showAdditionalFilters: boolean;
  setShowAdditionalFilters: (value: boolean) => void;
  filtersChanged: boolean;
  handleRefresh: () => void;
  handleClearFilters: () => void;
}

const toOptions = (arr: any[]) => (arr || []).map((v: any) => ({ value: String(v), label: String(v) }));

/** Renders projects page filter controls while preserving existing state shape. */
const ProjectsTopFilters: React.FC<ProjectsTopFiltersProps> = ({
  currentFilters,
  setCurrentFilters,
  filterOptions,
  availableSubBus,
  filterOptionsLoading,
  subBuLoading,
  filterOptionsError,
  refetchFilters,
  showAdditionalFilters,
  setShowAdditionalFilters,
  filtersChanged,
  handleRefresh,
  handleClearFilters,
}) => {
  const { user } = useAuth();
  const yearOpts = useMemo(() => toOptions(filterOptions.opportunity_closed_years), [filterOptions.opportunity_closed_years]);
  const quarterOpts = useMemo(() => toOptions(QUARTERS), []);
  const buOpts = useMemo(() => {
    const bus = hasAdminAccess(user)
      ? filterOptions.delivery_bus || []
      : excludeUnallocatedFromBus(filterOptions.delivery_bus);
    return toOptions(sortBusinessUnits(bus));
  }, [filterOptions.delivery_bus, user]);
  const subBuOpts = useMemo(() => toOptions(availableSubBus), [availableSubBus]);
  const accountOpts = useMemo(() => toOptions(filterOptions.accounts), [filterOptions.accounts]);
  const accountBuOpts = useMemo(() => toOptions(filterOptions.project_account_bus), [filterOptions.project_account_bus]);
  const ownerOpts = useMemo(() => toOptions(filterOptions.project_owners), [filterOptions.project_owners]);
  const statusOpts = useMemo(() => toOptions(filterOptions.statuses), [filterOptions.statuses]);
  const ragOpts = useMemo(() => toOptions(filterOptions.rag_statuses), [filterOptions.rag_statuses]);
  const effortsOpts = useMemo(() => toOptions(filterOptions.efforts_deviation), [filterOptions.efforts_deviation]);
  const fundedOpts = useMemo(() => toOptions(filterOptions.funded_by), [filterOptions.funded_by]);

  const trailingChips = (
    <>
      {filterOptionsLoading && (
        <Chip
          icon={<InlineSpinner size={16} thickness={5} />}
          label="Loading filters..."
          size="small"
          sx={{ backgroundColor: alpha(SEARCE_COLORS.primary, 0.1), color: SEARCE_COLORS.primary, '& .MuiChip-icon': { color: SEARCE_COLORS.primary } }}
        />
      )}
      {filterOptionsError && (
        <Chip label="Retry loading filters" size="small" color="error" variant="outlined" onClick={() => refetchFilters()} sx={{ cursor: 'pointer' }} />
      )}
    </>
  );

  const colBase = {
    flex: '1 1 180px',
    minWidth: 140,
    maxWidth: 240,
    display: 'flex',
    alignItems: 'flex-end',
  } as const;

  const primaryFilters = (
    <>
      <Box sx={colBase}><SelectAllMultiSelect label="Opportunity Closed Year" options={yearOpts} selectedValues={(currentFilters.opportunity_closed_years || []).map(String)} onChange={(vals) => setCurrentFilters({ ...currentFilters, opportunity_closed_years: vals.map(Number) })} loading={filterOptionsLoading} enableSelectAll /></Box>
      <Box sx={colBase}><SelectAllMultiSelect label="Opportunity Closed Quarter" options={quarterOpts} selectedValues={currentFilters.opportunity_closed_quarters || []} onChange={(vals) => setCurrentFilters({ ...currentFilters, opportunity_closed_quarters: vals })} loading={filterOptionsLoading} enableSelectAll /></Box>
      <Box sx={colBase}><SelectAllMultiSelect label="Business Unit" options={buOpts} selectedValues={currentFilters.delivery_bus || []} onChange={(vals) => setCurrentFilters((prev) => ({ ...prev, delivery_bus: vals, delivery_sub_bus: [] }))} loading={filterOptionsLoading} enableSelectAll /></Box>
      <Box sx={colBase}><SelectAllMultiSelect label="Sub Business Unit" options={subBuOpts} selectedValues={currentFilters.delivery_sub_bus || []} onChange={(vals) => setCurrentFilters({ ...currentFilters, delivery_sub_bus: vals })} loading={filterOptionsLoading || subBuLoading} enableSelectAll /></Box>
      <Box sx={colBase}><SelectAllMultiSelect label="Account Name" options={accountOpts} selectedValues={currentFilters.accounts || []} onChange={(vals) => setCurrentFilters({ ...currentFilters, accounts: vals })} loading={filterOptionsLoading} enableSearch searchPlaceholder="Search accounts..." emptyMessage="No accounts found" /></Box>
      <Box sx={colBase}><SelectAllMultiSelect label="Account BU" options={accountBuOpts} selectedValues={currentFilters.project_account_bus || []} onChange={(vals) => setCurrentFilters({ ...currentFilters, project_account_bus: vals })} loading={filterOptionsLoading} enableSelectAll /></Box>
    </>
  );

  const additionalFlex = { flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 6px)', md: '1 1 0' }, minWidth: 0 };
  const additionalFiltersContent = (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'flex-end', pt: 1 }}>
      <Box sx={additionalFlex}><SelectAllMultiSelect label="Project Owner" options={ownerOpts} selectedValues={currentFilters.project_owners || []} onChange={(vals) => setCurrentFilters({ ...currentFilters, project_owners: vals })} loading={filterOptionsLoading} enableSearch searchPlaceholder="Search project owners..." emptyMessage="No project owners found" /></Box>
      <Box sx={additionalFlex}><SelectAllMultiSelect label="Status" options={statusOpts} selectedValues={currentFilters.statuses || []} onChange={(vals) => setCurrentFilters({ ...currentFilters, statuses: vals })} loading={filterOptionsLoading} enableSelectAll /></Box>
      <Box sx={additionalFlex}><SelectAllMultiSelect label="Overall RAG Status" options={ragOpts} selectedValues={currentFilters.rag_statuses || []} onChange={(vals) => setCurrentFilters({ ...currentFilters, rag_statuses: vals })} loading={filterOptionsLoading} enableSelectAll /></Box>
      <Box sx={additionalFlex}><SelectAllMultiSelect label="Efforts Deviation" options={effortsOpts} selectedValues={currentFilters.efforts_deviation || []} onChange={(vals) => setCurrentFilters({ ...currentFilters, efforts_deviation: vals })} loading={filterOptionsLoading} enableSelectAll /></Box>
      <Box sx={additionalFlex}><SelectAllMultiSelect label="Funded By" options={fundedOpts} selectedValues={currentFilters.funded_by || []} onChange={(vals) => setCurrentFilters({ ...currentFilters, funded_by: vals })} loading={filterOptionsLoading} enableSelectAll /></Box>
    </Box>
  );

  return (
    <Box>
      <FilterStripLayout
        onClear={handleClearFilters}
        onRefresh={handleRefresh}
        hasUnsavedChanges={filtersChanged}
        trailingActions={trailingChips}
        dense
      >
        {primaryFilters}
      </FilterStripLayout>
      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            width: 'fit-content',
            '&:hover': { opacity: 0.8 },
          }}
          onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
          role="button"
          aria-expanded={showAdditionalFilters}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowAdditionalFilters(!showAdditionalFilters);
            }
          }}
        >
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.primary, fontWeight: 500, userSelect: 'none' }}>
            {showAdditionalFilters ? 'Hide Additional Filters' : 'Show Additional Filters'}
          </Typography>
          {showAdditionalFilters ? (
            <ExpandLessIcon sx={{ color: SEARCE_COLORS.primary, fontSize: 20 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: SEARCE_COLORS.primary, fontSize: 20 }} />
          )}
        </Box>
        <Collapse in={showAdditionalFilters}>{additionalFiltersContent}</Collapse>
      </Box>
    </Box>
  );
};

export default ProjectsTopFilters;
